import { useMemo, useState } from 'react';
import { collapsePills, PillLinkChip, PillRow } from 'src/components/Pill';
import {
  useDeleteConversationAttachment,
  useMoveAttachmentToSiteFiles,
} from 'src/graphql/entities/files-upload';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import type { SidePanelRowProps } from 'src/graphql/entities/side-panel-personalization';
import { AttachmentPill } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-pill';
import {
  type AttachmentPillModel,
  mapConversationAttachmentPills,
} from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments/attachment-utils';
import { useConversationEntity } from 'src/pages/Conversations/SidePanel/ConversationDetails/properties/conversation-entity-context';
// From its own module, not the `src/utils/hooks` barrel. This hook reaches the `src/components`
// barrel (via ConfirmationModal), which re-enters the entity gql modules and breaks fragment
// interpolation under vitest ("Unexpected Name undefined"). Importing the narrow path lets the
// suites for this component mock just this hook instead of the whole barrel.
import { useConfirmationModal } from 'src/utils/hooks/use-confirmation-modal';

const MIN_VISIBLE_ATTACHMENT_PILLS = 4;

export const AttachmentsRow = (_props: SidePanelRowProps) => {
  const { conversation } = useConversationEntity();
  const { user } = useCurrentUserProfile();
  const { moveToSiteFiles } = useMoveAttachmentToSiteFiles();
  const { deleteAttachment, canDeleteAttachments } = useDeleteConversationAttachment();
  const { confirm } = useConfirmationModal();
  const [expanded, setExpanded] = useState(false);

  const siteAddressId = conversation.associatedAddresses?.[0]?.addressId;

  const attachments = useMemo(
    () => mapConversationAttachmentPills(conversation.attachments),
    [conversation.attachments],
  );

  const { visible, hiddenCount, isCollapsible } = useMemo(
    () =>
      collapsePills({
        items: attachments,
        minVisible: MIN_VISIBLE_ATTACHMENT_PILLS,
        expanded,
      }),
    [attachments, expanded],
  );

  const confirmAndDeleteAttachment = async (attachment: AttachmentPillModel) => {
    const confirmed = await confirm({
      title: 'Delete file?',
      body: `"${attachment.fileName}" will be deleted permanently.`,
      confirmLabel: 'Delete file',
      danger: true,
    });

    if (confirmed) {
      await deleteAttachment(attachment.id);
    }
  };

  if (!attachments.length) {
    return null;
  }

  return (
    <PillRow label="Attachments">
      {visible.map((attachment) => (
        <AttachmentPill
          key={attachment.id}
          attachment={attachment}
          onAddToSiteFiles={
            siteAddressId
              ? () =>
                  moveToSiteFiles({
                    attachment: attachment.siteFilePayload,
                    addressId: siteAddressId,
                    uploadedByUserId: user?.id,
                  })
              : undefined
          }
          onDelete={
            canDeleteAttachments ? () => confirmAndDeleteAttachment(attachment) : undefined
          }
        />
      ))}

      {isCollapsible ? (
        <PillLinkChip type="button" onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? 'Show less' : `+${hiddenCount} more`}
        </PillLinkChip>
      ) : null}
    </PillRow>
  );
};
